import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { ApiError } from '../lib/api.js';
import { BigButton } from '../components/BigButton.js';
import { Spinner } from '../components/Spinner.js';
import { TrainingStrip } from '../components/TrainingStrip.js';

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const { user, status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const justReset = searchParams.get('reset') === '1';
  const justExpired = !justReset && searchParams.get('expired') === '1';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'loading') {
    return <Spinner label="Checking your session" />;
  }
  if (user) {
    const to = (location.state as LocationState | null)?.from?.pathname ?? '/sell';
    return <Navigate to={to} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
      navigate('/sell', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <TrainingStrip />
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <h1 className="text-center font-heading font-semibold text-3xl text-ink">
            Divergent Works
          </h1>
          <p className="mt-1 text-center font-body text-lg text-muted">
            Our business management tool
          </p>
          <p className="text-center font-body text-base text-muted mt-4 mb-8">
            Sign in to start selling
          </p>

          {justReset && (
            <p className="mb-6 rounded-lg border border-border bg-aqua px-4 py-3 text-center font-body text-sm text-teal-dark">
              Training data was cleared.
            </p>
          )}

          {justExpired && (
            <p className="mb-6 rounded-lg border border-border bg-aqua px-4 py-3 text-center font-body text-sm text-teal-dark">
              You were signed out after 2 hours of inactivity. Please sign in again.
            </p>
          )}

          <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4" noValidate>
            <div>
              <label
                htmlFor="username"
                className="block font-body font-semibold text-base text-teal-dark mb-1"
              >
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full min-h-12 rounded-lg border-2 border-border px-4 text-lg font-body bg-surface"
                required
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block font-body font-semibold text-base text-teal-dark mb-1"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full min-h-12 rounded-lg border-2 border-border px-4 pr-20 text-lg font-body bg-surface"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 min-h-12 px-3 rounded-md font-body font-semibold text-base text-teal-dark"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {error && (
              <p role="alert" className="font-body text-sm text-danger">
                {error}
              </p>
            )}

            <BigButton type="submit" disabled={submitting} className="w-full mt-2">
              {submitting ? 'Signing in…' : 'Sign in'}
            </BigButton>
          </form>
        </div>
      </main>
    </div>
  );
}
